import * as React from "react";
import { DayPicker } from "react-day-picker";
import { ChevronLeft, ChevronRight } from "lucide-react";

import { cn } from "@/lib/utils";

import "react-day-picker/style.css";

export type CalendarProps = React.ComponentProps<typeof DayPicker>;

export function Calendar({
  className,
  classNames,
  showOutsideDays = true,
  ...props
}: CalendarProps) {
  return (
    <DayPicker
      showOutsideDays={showOutsideDays}
      className={cn(
        "p-3 text-[13px] [--rdp-accent-color:rgb(34_197_94)] [--rdp-accent-background-color:rgb(34_197_94_/_0.16)] [--rdp-day-height:2.5rem] [--rdp-day-width:2.5rem] [--rdp-day_button-height:2.25rem] [--rdp-day_button-width:2.25rem] [--rdp-day_button-border:1px_solid_transparent] [--rdp-day_button-border-radius:9999px] [--rdp-selected-border:1px_solid_transparent] [--rdp-outside-opacity:1] [--rdp-disabled-opacity:0.55]",
        className
      )}
      classNames={{
        months: "flex flex-col sm:flex-row gap-3",
        month: "space-y-4",
        month_caption: "relative flex h-8 items-center justify-center",
        caption_label: "text-[13px] font-semibold text-foreground",
        nav: "flex items-center gap-1",
        button_previous: cn(
          "absolute left-0 inline-flex h-8 w-8 items-center justify-center rounded-md border border-border/55 bg-background/80 p-0 text-muted-foreground shadow-none transition-colors hover:bg-accent hover:text-foreground"
        ),
        button_next: cn(
          "absolute right-0 inline-flex h-8 w-8 items-center justify-center rounded-md border border-border/55 bg-background/80 p-0 text-muted-foreground shadow-none transition-colors hover:bg-accent hover:text-foreground"
        ),
        month_grid: "w-full border-collapse",
        weekdays: "flex",
        weekday:
          "w-10 rounded-md text-center text-[11px] font-semibold text-muted-foreground",
        week: "mt-1 flex w-full",
        day: "h-10 w-10 p-0 text-center text-[13px]",
        day_button: cn(
          "inline-flex h-9 w-9 items-center justify-center rounded-full border border-transparent bg-transparent p-0 text-[13px] font-medium text-foreground transition-colors hover:bg-accent/80 hover:text-accent-foreground",
          "focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-400/50"
        ),
        selected:
          "text-[13px] font-medium [&>button]:border-emerald-400/35 [&>button]:bg-emerald-500 [&>button]:text-white [&>button]:font-semibold [&>button]:shadow-[0_10px_24px_-16px_rgba(34,197,94,0.95)] hover:[&>button]:bg-emerald-500",
        today:
          "text-[13px] font-medium [&>button]:border-emerald-500/20 [&>button]:bg-emerald-500/10 [&>button]:text-emerald-300",
        outside: "text-muted-foreground/45",
        disabled: "text-muted-foreground/40",
        range_middle:
          "[&>button]:rounded-none [&>button]:bg-emerald-500/12 [&>button]:text-foreground",
        range_start:
          "rounded-l-full [&>button]:bg-emerald-500 [&>button]:text-white",
        range_end:
          "rounded-r-full [&>button]:bg-emerald-500 [&>button]:text-white",
        hidden: "invisible",
        ...classNames,
      }}
      components={{
        Chevron: ({ className: iconClassName, orientation }) => {
          const Icon =
            orientation === "left"
              ? ChevronLeft
              : orientation === "right"
                ? ChevronRight
                : ChevronLeft;

          return <Icon className={cn("h-4 w-4", iconClassName)} />;
        },
      }}
      {...props}
    />
  );
}
